import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import api from '../services/api';
import './ServiceCalls.css';
export function CallSettings() {
  const [config, setConfig] = useState(null),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [categories, setCategories] = useState('');
  useEffect(() => {
    api.get('/service-calls/settings').then(({
      data
    }) => {
      setConfig(data);
      setCategories(data.categories.join('\n'));
    }).catch(e => setError(e.response?.data?.message || 'Unable to load Call settings.'));
  }, []);
  const save = async value => {
    setBusy(true);
    setError('');
    try {
      const {
        data
      } = await api.put('/service-calls/settings', value);
      setConfig(data);
    } catch (e) {
      setError(e.response?.data?.message || 'Unable to save Call settings.');
    } finally {
      setBusy(false);
    }
  };
  return <section className="border rounded-3 p-3 my-4"><h2 className="h5">Call · Customer assistance</h2>{error && <Alert variant="danger">{error}</Alert>}{config && <><Form.Check id="enable-service-calls" type="switch" label="Enable customer calls" checked={config.enabled} disabled={busy} onChange={e => save({
        ...config,
        enabled: e.target.checked
      })} /><div className="d-flex flex-wrap gap-3 my-3">{['TABLE', 'ROOM', 'DEPARTMENT'].map(module => <Form.Check id={'call-module-'+module} key={module} label={module.toLowerCase()} checked={config.modules.includes(module)} disabled={busy} onChange={e => save({
          ...config,
          modules: e.target.checked ? [...config.modules, module] : config.modules.filter(m => m !== module)
        })} />)}</div><Form.Check className="mb-3" id="call-resolution-required" type="switch" label="Require a note when marking a call solved" checked={Boolean(config.resolutionNoteRequired)} disabled={busy} onChange={e=>save({...config,resolutionNoteRequired:e.target.checked})}/><Form.Label htmlFor="call-categories">Reasons for calling · one per line</Form.Label><Form.Control id="call-categories" as="textarea" rows={5} value={categories} onChange={e => setCategories(e.target.value)} /><Form.Text>Other is always available for a written issue. Customers choose Normal, Urgent or Emergency.</Form.Text><div className="mt-3"><Button disabled={busy} onClick={() => save({
          ...config,
          categories: categories.split('\n').map(s => s.trim()).filter(Boolean)
        })}>Save call reasons</Button></div></>}</section>;
}
export function CustomerCall({
  orderNumber,
  credentials = {}
}) {
  const [options, setOptions] = useState(null),
    [open, setOpen] = useState(false),
    [urgency, setUrgency] = useState('NORMAL'),
    [category, setCategory] = useState(''),
    [issue, setIssue] = useState(''),
    [error, setError] = useState(''),
    [message, setMessage] = useState(''),
    [activeCallId, setActiveCallId] = useState(null),
    [busy, setBusy] = useState(false);
  const signature = JSON.stringify(credentials);
  useEffect(() => {
    let active = true;
    api.post('/service-calls/options', {
      orderNumber,
      ...JSON.parse(signature)
    }, {
      skipAuthError: true
    }).then(({
      data
    }) => {
      if (active) {
        setOptions(data);
        setCategory(data.reasonGroups?.NORMAL?.[0] || data.categories[0] || 'Other');
      }
    }).catch(() => {});
    return () => {
      active = false;
    };
  }, [orderNumber, signature]);
  useEffect(() => {
    setActiveCallId(null);setMessage('');setOpen(false);
  }, [orderNumber, signature]);
  useEffect(() => {
    if (!activeCallId) return;
    let active=true,running=false;
    const controller=new AbortController();
    const poll=async()=>{
      if(!active||running)return;
      running=true;
      try {
        const {data}=await api.post('/service-calls/status',{orderNumber,...JSON.parse(signature),callId:activeCallId},{skipAuthError:true,signal:controller.signal});
        if(active&&data.status==='RESOLVED'){setOpen(false);setActiveCallId(null);setMessage('');setIssue('');setError('');}
      } catch { /* A temporary connection failure must not mark a call solved. */ }
      finally {running=false;}
    };
    poll();const timer=setInterval(poll,3000);
    return()=>{active=false;controller.abort();clearInterval(timer);};
  }, [activeCallId,orderNumber,signature]);
  if (!options?.enabled) return null;
  return <><button type="button" aria-label="Call for assistance" onClick={() => setOpen(true)} style={{
      position: 'fixed',
      bottom: 'max(24px, env(safe-area-inset-bottom))',
      right: 24,
      zIndex: 1040,
      width: 60,
      height: 60,
      borderRadius: '50%',
      border: '2px solid white',
      background: '#b31324',
      color: 'white',
      boxShadow: '0 6px 24px #0004',
      fontSize: 26
    }}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M9 21h6" /></svg><span className="visually-hidden">Call</span></button><Modal show={open} onHide={() => !busy && setOpen(false)} centered scrollable dialogClassName="customer-call-dialog"><Modal.Header closeButton><Modal.Title>Call for assistance</Modal.Title></Modal.Header><Form onSubmit={async e => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
          const {
            data
          } = await api.post('/service-calls', {
            orderNumber,
            ...credentials,
            urgency,
            category,
            issue: category === 'Other' ? issue.trim() : ''
          }, {
            skipAuthError: true
          });
          setActiveCallId(data.id);
          setMessage('Call ' + data.token + ' sent. Your team has been notified.');
        } catch (err) {
          setError(err.response?.data?.message || 'Unable to send call.');
        } finally {
          setBusy(false);
        }
      }}><Modal.Body>{error && <Alert variant="danger">{error}</Alert>}{message && <Alert variant="success">{message}</Alert>}<fieldset className="call-choice-fieldset" disabled={busy || Boolean(message)}><legend>Urgency</legend><div className="call-urgency-options">{[{value:'NORMAL',label:'Normal',hint:'General assistance'},{value:'URGENT',label:'Urgent',hint:'Help needed soon'},{value:'EMERGENCY',label:'Emergency',hint:'Immediate assistance'}].map(level=><label key={level.value} className={'call-urgency-choice call-urgency-'+level.value.toLowerCase()}><input type="radio" name="call-urgency" value={level.value} checked={urgency===level.value} onChange={()=>{setUrgency(level.value);setCategory(options.reasonGroups?.[level.value]?.[0] || options.categories[0] || 'Other');setIssue('');}}/><span className="call-urgency-content"><svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M9 19a3 3 0 0 0 6 0"/></svg></span><strong className="call-urgency-caption">{level.label}</strong></label>)}</div></fieldset><fieldset className="call-choice-fieldset mt-4" disabled={busy || Boolean(message)}><legend>Why are you calling?</legend><div className="call-reason-options">{[...(options.reasonGroups?.[urgency] || options.categories),'Other'].map((reason,index)=><label className="call-reason-choice" key={reason}><input type="radio" id={'call-reason-'+index} name="call-reason" value={reason} checked={category===reason} onChange={()=>{setCategory(reason);setIssue('');}}/><span>{reason}</span></label>)}</div></fieldset>{category==='Other' && <div className="mt-3"><Form.Label htmlFor="call-issue">Reason (required)</Form.Label><Form.Control id="call-issue" as="textarea" rows={3} maxLength={1000} minLength={3} required value={issue} disabled={busy || Boolean(message)} onChange={e=>setIssue(e.target.value)} placeholder="Tell us what you need help with"/></div>}</Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>Close</Button><Button type="submit" disabled={busy || Boolean(message)}>Ring bell</Button></Modal.Footer></Form></Modal></>;
}
export function ManagerCalls() {
  const [calls,setCalls]=useState([]), [selected,setSelected]=useState(null), [note,setNote]=useState(''), [error,setError]=useState(''), [busy,setBusy]=useState(false);
  const audio=useRef(null);
  const pending=calls.find(row=>row.status==='OPEN');
  const active=calls.find(row=>row._id===selected && row.status==='ACKNOWLEDGED');
  const load=useCallback(async()=>{const {data}=await api.get('/service-calls');setCalls(data);},[]);
  useEffect(()=>{
    let stopped=false,running=false;
    const poll=async()=>{if(stopped||running)return;running=true;try{const {data}=await api.get('/service-calls');if(!stopped)setCalls(data);}catch(e){if(!stopped)setError(e.response?.data?.message||'Unable to check calls.');}finally{running=false;}};
    poll();const timer=setInterval(poll,5000);return()=>{stopped=true;clearInterval(timer);};
  },[]);
  useEffect(()=>{
    const unlock=()=>{const Audio=window.AudioContext||window.webkitAudioContext;if(!audio.current&&Audio)audio.current=new Audio();audio.current?.resume().catch(()=>{});};
    window.addEventListener('pointerdown',unlock);window.addEventListener('keydown',unlock);
    return()=>{window.removeEventListener('pointerdown',unlock);window.removeEventListener('keydown',unlock);audio.current?.close();};
  },[]);
  const pendingId=pending?._id;
  useEffect(()=>{
    if(!pendingId)return;
    const ring=()=>{const ctx=audio.current;if(!ctx||ctx.state!=='running')return;for(let i=0;i<3;i++){const oscillator=ctx.createOscillator(),gain=ctx.createGain(),start=ctx.currentTime+i*.28;oscillator.connect(gain);gain.connect(ctx.destination);oscillator.frequency.value=i%2?880:660;gain.gain.setValueAtTime(0,start);gain.gain.linearRampToValueAtTime(.12,start+.02);gain.gain.linearRampToValueAtTime(0,start+.22);oscillator.start(start);oscillator.stop(start+.23);}};
    ring();const timer=setInterval(ring,2000);return()=>clearInterval(timer);
  },[pendingId]);
  const update=async(row,status)=>{
    setBusy(true);setError('');try{await api.patch('/service-calls/'+row._id,{status,...(status==='RESOLVED'?{resolutionNote:note.trim()}:{})});
      if(status==='ACKNOWLEDGED'){setSelected(row._id);setNote('');}else{setSelected(null);setNote('');}
      await load();
    }catch(e){setError(e.response?.data?.message||'Unable to update call.');}finally{setBusy(false);}
  };
  return <>
    {pending && !active && <Modal show fullscreen backdrop="static" keyboard={false} contentClassName="incoming-call-screen" aria-labelledby="incoming-call-title">
      <Modal.Body className="d-flex flex-column justify-content-center align-items-center text-center p-4">
        <svg className="incoming-call-icon" width="90" height="90" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M9 19a3 3 0 0 0 6 0"/></svg>
        <p className="fs-5 fw-bold mt-4">{pending.urgency}</p><h1 id="incoming-call-title" className="display-3 fw-bold">Calling</h1>
        <h2 className="display-5 fw-bold my-3">{pending.module==='TABLE'?'Table':pending.module==='ROOM'?'Room':'Department'} {pending.location}</h2>
        <p className="fs-4">{pending.category}</p><p>Order {pending.orderNumber}</p>
        {error&&<Alert variant="light">{error}</Alert>}
        <Button size="lg" variant="light" className="incoming-call-accept" disabled={busy} onClick={()=>update(pending,'ACKNOWLEDGED')}>{busy?'Accepting…':'Accept'}</Button>
      </Modal.Body>
    </Modal>}
    <Modal show={Boolean(active)} onHide={()=>{if(!busy){setSelected(null);setError('');}}} centered scrollable dialogClassName="customer-call-dialog" aria-labelledby="accepted-call-title">
      <Modal.Header closeButton><Modal.Title id="accepted-call-title">Call details</Modal.Title></Modal.Header>
      {active&&<Form className="d-flex flex-column overflow-hidden" onSubmit={e=>{e.preventDefault();update(active,'RESOLVED');}}>
        <Modal.Body>{error&&<Alert variant="danger">{error}</Alert>}<div className="d-flex flex-wrap justify-content-between gap-3 mb-3"><strong>{active.module} · {active.location}</strong><span className="badge bg-danger">{active.urgency}</span></div>
          <dl className="row"><dt className="col-4">Order No</dt><dd className="col-8">{active.orderNumber}</dd><dt className="col-4">Call time</dt><dd className="col-8"><time dateTime={active.createdAt}>{new Date(active.createdAt).toLocaleString()}</time></dd><dt className="col-4">Reason</dt><dd className="col-8">{active.category}</dd></dl>
          {active.issue&&<p style={{whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>{active.issue}</p>}
          <h3 className="h6 fw-bold">Ordered dishes</h3>{active.items?.length?<div className="table-responsive"><table className="table align-middle"><thead><tr><th>Dish</th><th>Serving size</th><th className="text-end">Qty</th></tr></thead><tbody>{active.items.map((item,index)=><tr key={index}><td>{item.name}</td><td>{item.portion||'—'}</td><td className="text-end">{item.quantity}</td></tr>)}</tbody></table></div>:<p className="text-muted">Order item information is unavailable.</p>}
          <Form.Label htmlFor="call-resolution">Resolution note {active.resolutionNoteRequired?'(required)':'(optional)'}</Form.Label><Form.Control id="call-resolution" as="textarea" rows={3} maxLength={1000} minLength={active.resolutionNoteRequired?3:undefined} required={active.resolutionNoteRequired} disabled={busy} value={note} onChange={e=>setNote(e.target.value)}/>
        </Modal.Body><Modal.Footer><Button variant="secondary" disabled={busy} onClick={()=>setSelected(null)}>Close</Button><Button type="submit" disabled={busy}>{busy?'Saving…':'Solved'}</Button></Modal.Footer>
      </Form>}
    </Modal>
    {!active&&!pending&&calls.some(row=>row.status==='ACKNOWLEDGED')&&<Button className="manager-call-bell" aria-label="View accepted customer calls" onClick={()=>{setSelected(calls.find(row=>row.status==='ACKNOWLEDGED')._id);setNote('');}}><svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4"/></svg></Button>}
    {active&&calls.filter(row=>row.status==='ACKNOWLEDGED'&&row._id!==active._id).length>0&&<div className="visually-hidden" aria-live="polite">More accepted calls are waiting.</div>}
  </>;
}
